import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { useUrlFilters } from "@/lib/urlfilters";
import { fmtDT } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const NEXT = {
  submitted: [["under_review", "Start review"]],
  under_review: [["clarification", "Request clarification"], ["approved", "Approve"]],
  clarification: [["under_review", "Back to review"]],
  approved: [["closed", "Close"]],
};

export default function Incidents() {
  const [rows, setRows] = useState([]);
  // Reached from the dashboard's OPEN INCIDENTS tile as ?status=open.
  const [f, setF] = useUrlFilters({ status: "all", severity: "all" });
  const [sites, setSites] = useState([]);
  const [config, setConfig] = useState(null);
  const [sel, setSel] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ site_id: "", type: "Incident", severity: "low", title: "", description: "" });
  const [photo, setPhoto] = useState(null);
  const [amendOpen, setAmendOpen] = useState(false);
  const [amendForm, setAmendForm] = useState({ description: "", reason: "" });

  const load = () => api.get("/incidents").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const create = async () => {
    try {
      let photos = [];
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photos = [data.path];
      }
      await api.post("/incidents", { ...form, photos });
      toast.success("Report submitted");
      setNewOpen(false);
      setForm({ site_id: "", type: "Incident", severity: "low", title: "", description: "" });
      setPhoto(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const transition = async (to) => {
    try {
      const { data } = await api.post(`/incidents/${sel.id}/transition`, { to });
      toast.success(`Status → ${to}`);
      setSel(data);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const acknowledge = async () => {
    try {
      await api.post(`/incidents/${sel.id}/acknowledge`);
      toast.success("Acknowledged");
      const { data } = await api.get(`/incidents/${sel.id}`);
      setSel(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const submitAmend = async () => {
    try {
      const { data } = await api.post(`/incidents/${sel.id}/amend`, amendForm);
      toast.success("Amendment versioned — original preserved");
      setSel(data);
      setAmendOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const shownIncidents = rows.filter((r) => {
    const st = f.status === "all" ? true
      : f.status === "open" ? !["approved", "closed"].includes(r.status)
        : r.status === f.status;
    const sv = f.severity === "all" ? true
      : f.severity === "high_critical" ? ["high", "critical"].includes(r.severity)
        : r.severity === f.severity;
    return st && sv;
  });

  return (
    <div data-testid="incidents-page">
      <PageHeader title="Incident & Activity Reports" sub="Immutable originals · versioned corrections · escalation tracking"
        actions={<Button data-testid="new-incident-button" className="rounded-none" onClick={() => setNewOpen(true)}>New report</Button>} />
      {/* ⚠️ "Open" here is every state that is NOT approved or closed — the
          same definition the dashboard tile counts. Two screens disagreeing
          about what open means is worse than neither showing it. */}
      <div className="flex flex-wrap gap-2 mb-3 items-end" data-testid="incident-filters">
        <div className="w-56">
          <Label className="text-[10px] uppercase font-bold">Status</Label>
          <Select value={f.status} onValueChange={(v) => setF({ status: v })}>
            <SelectTrigger data-testid="incident-status-filter" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="open">Open (not approved or closed)</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="under_review">Under review</SelectItem>
              <SelectItem value="clarification">Clarification</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Label className="text-[10px] uppercase font-bold">Severity</Label>
          <Select value={f.severity} onValueChange={(v) => setF({ severity: v })}>
            <SelectTrigger data-testid="incident-severity-filter" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any severity</SelectItem>
              <SelectItem value="high_critical">High or critical</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DataTable testid="incidents-table" onRowClick={async (r) => { const { data } = await api.get(`/incidents/${r.id}`); setSel(data); }} rows={shownIncidents}
        columns={[
          { key: "ref_no", label: "Ref", render: (r) => <span className="font-mono text-xs">{r.ref_no}</span> },
          { key: "title", label: "Report", render: (r) => <div><div className="font-semibold">{r.title}</div><div className="text-xs text-muted-foreground">{r.site_name}</div></div> },
          { key: "type", label: "Type" },
          { key: "severity", label: "Severity", render: (r) => <StatusBadge value={r.severity} /> },
          { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
          { key: "versions", label: "Versions", render: (r) => <span className="font-mono text-xs">v{(r.versions || []).length}</span> },
          { key: "created_at", label: "Filed", render: (r) => <span className="font-mono text-xs">{fmtDT(r.created_at)}</span> },
        ]} />

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="rounded-none max-h-[85vh] overflow-auto" data-testid="new-incident-dialog">
          <DialogHeader><DialogTitle className="font-heading">New report</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold">Site</Label>
              <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                <SelectTrigger data-testid="incident-site" className="rounded-none mt-1"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="incident-type" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{(config?.report_types || ["Incident"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Severity</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="incident-severity" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{(config?.severities || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.type === "Incident" && (
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider font-bold">Category</Label>
                <Select value={form.category || ""} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="incident-category" className="rounded-none mt-1"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{(config?.incident_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2"><Label className="text-xs uppercase tracking-wider font-bold">Title</Label><Input data-testid="incident-title" className="rounded-none mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="col-span-2"><Label className="text-xs uppercase tracking-wider font-bold">Description</Label><Textarea data-testid="incident-description" className="rounded-none mt-1 min-h-[100px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="col-span-2"><Label className="text-xs uppercase tracking-wider font-bold">Photo (optional)</Label><Input data-testid="incident-photo" type="file" accept="image/*" className="rounded-none mt-1" onChange={(e) => setPhoto(e.target.files?.[0] || null)} /></div>
          </div>
          <Button data-testid="incident-create-submit" className="w-full rounded-none mt-3" onClick={create} disabled={!form.site_id || !form.title || !form.description}>Submit report</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sel} onOpenChange={() => setSel(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-auto" data-testid="incident-detail-dialog">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">{sel.ref_no} <StatusBadge value={sel.severity} /> <StatusBadge value={sel.status} /></DialogTitle>
              </DialogHeader>
              <div className="text-sm font-semibold">{sel.title}</div>
              <div className="font-mono text-[10px] text-muted-foreground mb-3">{sel.site_name} · {fmtDT(sel.happened_at)} · by {sel.reported_by}</div>
              <div className="border border-border p-3 text-sm whitespace-pre-wrap mb-3" data-testid="incident-current-text">{sel.description}</div>
              {(sel.photos || []).length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {sel.photos.map((p) => <img key={p} src={`${API}/files/${p}`} alt="evidence" className="border border-border object-cover h-28 w-full" data-testid="incident-photo-img" />)}
                </div>
              )}
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Version history (original immutable)</div>
              {(sel.versions || []).map((v) => (
                <div key={v.version} className="border-t border-border py-1 text-xs">
                  <span className="font-mono">v{v.version}</span> · {v.by} · {fmtDT(v.at)} — <span className="text-muted-foreground">{v.reason}</span>
                </div>
              ))}
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mt-3 mb-1">Acknowledged by ({(sel.acknowledged_by || []).length})</div>
              {(sel.acknowledged_by || []).map((a, i) => <div key={i} className="text-xs font-mono">{a.name} · {fmtDT(a.at)}</div>)}
              <div className="flex flex-wrap gap-2 mt-4">
                <Button size="sm" variant="outline" className="rounded-none" data-testid="incident-ack" onClick={acknowledge}>Acknowledge</Button>
                <Button size="sm" variant="outline" className="rounded-none" data-testid="incident-amend" onClick={() => { setAmendForm({ description: sel.description, reason: "" }); setAmendOpen(true); }}>Amend (versioned)</Button>
                <a data-testid="incident-pdf" href={`${API}/incidents/${sel.id}/pdf`} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="rounded-none">PDF export</Button>
                </a>
                {(NEXT[sel.status] || []).map(([to, label]) => (
                  <Button key={to} size="sm" className="rounded-none" data-testid={`incident-to-${to}`} onClick={() => transition(to)}>{label}</Button>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={amendOpen} onOpenChange={setAmendOpen}>
        <DialogContent className="rounded-none" data-testid="amend-incident-dialog">
          <DialogHeader><DialogTitle className="font-heading">Amend {sel?.ref_no} — becomes v{(sel?.versions?.length || 0) + 1}</DialogTitle></DialogHeader>
          <Label className="text-xs uppercase tracking-wider font-bold">Corrected description</Label>
          <Textarea data-testid="amend-incident-text" className="rounded-none mt-1 min-h-[100px]" value={amendForm.description} onChange={(e) => setAmendForm({ ...amendForm, description: e.target.value })} />
          <Label className="text-xs uppercase tracking-wider font-bold mt-2">Reason (required, audited)</Label>
          <Input data-testid="amend-incident-reason" className="rounded-none mt-1" value={amendForm.reason} onChange={(e) => setAmendForm({ ...amendForm, reason: e.target.value })} />
          <Button data-testid="amend-incident-submit" className="w-full rounded-none mt-3" onClick={submitAmend} disabled={!amendForm.reason.trim()}>Save as new version</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
